import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  cachedAxiosGet,
  clearAllCache,
  clearCacheForSection,
  getCacheStats,
  getCacheTTLs,
  setUserCacheTTLs,
  setAdminCacheTTLs,
  resetUserCacheTTLs,
  DEFAULT_TTLS,
  SECTION_LABELS,
} from '../utils/cacheManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Use same-origin API to avoid cross-origin issues on custom domains (e.g., taskpiece.world)
const API = `${window.location.origin}/api`;

// Resilient POST helper: retries with exponential backoff, falls back to fetch API.
// Cloudflare 520/502 errors on production (taskpiece.world) are typically
// stale-pool issues that resolve in <100 ms once a new edge→origin connection
// is opened. We do a TIGHT first retry (250 ms) on those specific statuses
// and only escalate the wait if it keeps failing — this turns the previously
// jarring "instant 520 → 2 s buffer → success" UX into a fully invisible
// recovery for the user.
async function resilientPost(url, data, { timeout = 15000, maxRetries = 3 } = {}) {
  let lastError = null;
  // Backoff schedule per attempt index (in ms). Tight first retry kills the
  // typical instant-Cloudflare-520 pattern without making the user wait.
  const FAST_BACKOFF_MS = [0, 250, 800, 2000, 4000];
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    if (attempt > 0) {
      const wait = FAST_BACKOFF_MS[attempt] ?? 4000;
      await new Promise(r => setTimeout(r, wait));
    }
    try {
      const res = await axios.post(url, data, { timeout });
      return res;
    } catch (err) {
      lastError = err;
      const status = err.response?.status;
      // Retry on 502/520/503 (proxy errors) and network errors
      if (status && status !== 502 && status !== 520 && status !== 503) throw err;
      // On last attempt, try native fetch as fallback
      if (attempt === maxRetries - 1) {
        try {
          const ctrl = new AbortController();
          const timer = setTimeout(() => ctrl.abort(), timeout);
          const resp = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
            signal: ctrl.signal,
          });
          clearTimeout(timer);
          const json = await resp.json();
          if (!resp.ok) {
            const axiosLikeError = new Error(json.detail || 'Request failed');
            axiosLikeError.response = { status: resp.status, data: json };
            throw axiosLikeError;
          }
          return { data: json, status: resp.status };
        } catch (fetchErr) {
          if (fetchErr.response) throw fetchErr;
          throw lastError;
        }
      }
    }
  }
  throw lastError;
}

// Resilient GET helper
async function resilientGet(url, params, { timeout = 10000, maxRetries = 3 } = {}) {
  let lastError = null;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    if (attempt > 0) await new Promise(r => setTimeout(r, 2000 + 2000 * attempt));
    try {
      return await axios.get(url, { params, timeout });
    } catch (err) {
      lastError = err;
      const status = err.response?.status;
      if (status && status !== 502 && status !== 520 && status !== 503) throw err;
      if (attempt === maxRetries - 1) {
        try {
          const qs = params ? '?' + new URLSearchParams(params).toString() : '';
          const ctrl = new AbortController();
          const timer = setTimeout(() => ctrl.abort(), timeout);
          const resp = await fetch(url + qs, { signal: ctrl.signal });
          clearTimeout(timer);
          const json = await resp.json();
          if (!resp.ok) {
            const axiosLikeError = new Error(json.detail || 'Request failed');
            axiosLikeError.response = { status: resp.status, data: json };
            throw axiosLikeError;
          }
          return { data: json, status: resp.status };
        } catch (fetchErr) {
          if (fetchErr.response) throw fetchErr;
          throw lastError;
        }
      }
    }
  }
  throw lastError;
}

const AppContext = createContext(null);

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};

const ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'sub_admin', label: 'Sub Admin' },
  { value: 'hr', label: 'HR' },
  { value: 'accounts_head', label: 'Accounts Head' },
  { value: 'accounts_manager', label: 'Accounts Manager' },
  { value: 'business_head', label: 'Business Head' },
  { value: 'zonal_head', label: 'Zonal Head' },
  { value: 'sales_manager', label: 'Sales Manager' },
  { value: 'assistant_manager', label: 'Assistant Manager' },
  { value: 'sales_coordinator', label: 'Sales Coordinator' },
  { value: 'team_leader', label: 'Team Leader' },
  { value: 'sales_point_business', label: 'Sales Point (Business)' },
  { value: 'sales_point_individual', label: 'Sales Point (Individual)' },
  { value: 'operations', label: 'Operations' },
  { value: 'txn_monitor', label: 'Txn Monitor' },
];

export const ROLE_LABELS = Object.fromEntries(ROLES.map(r => [r.value, r.label]));
export const ROLE_OPTIONS = ROLES;

export const ROLE_HIERARCHY = {
  admin: 0, sub_admin: 0, accounts_head: 0,
  accounts_manager: 1, hr: 1, business_head: 1,
  zonal_head: 2,
  sales_manager: 3, master: 3,
  assistant_manager: 4, sales_coordinator: 4,
  monitor: 5, team_leader: 5, operations: 5,
  txn_monitor: 5,
  pos: 6, sales_point_business: 6, sales_point_individual: 6,
};

export const AppProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [seeded, setSeeded] = useState(false);
  const [roleFeatures, setRoleFeatures] = useState([]);
  const [departmentFeatures, setDepartmentFeatures] = useState([]);
  const [subUserPermissions, setSubUserPermissions] = useState({});  // { feature_key: "view"|"edit" }
  const [rolePermissions, setRolePermissions] = useState({});
  const [businessLines, setBusinessLines] = useState([]);
  const [allBusinessLines, setAllBusinessLines] = useState([]);
  const [blFeatureMap, setBlFeatureMap] = useState({}); // { featureKey: [businessLineId] } — SPECIFIC features
  const [salesDeptConfig, setSalesDeptConfig] = useState({ departments: [], linked_functions: [] });
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [featureFlags, setFeatureFlags] = useState({ revenue_export_enabled: true });
  const [bootstrapped, setBootstrapped] = useState(false);
  const [reverifyRequired, setReverifyRequired] = useState(false);

  // App-level cache for the user's accessible business places (RBAC-scoped).
  // Pre-fetched once after login so pickers (Travel Plan, Visit Planner) open
  // instantly. `placesLoadedAt` lets consumers decide whether to trust it.
  const [allPlaces, setAllPlaces] = useState([]);
  const [allPlacesLoading, setAllPlacesLoading] = useState(false);
  const [allPlacesError, setAllPlacesError] = useState(null);
  const [allPlacesLoadedAt, setAllPlacesLoadedAt] = useState(null);

  const identifyUser = useCallback(async (userData) => {
    try {
      const res = await axios.post(`${API}/auth/identify`, userData);
      setCurrentUser(res.data);
      return res.data;
    } catch (e) {
      console.error('Identity error:', e);
      return null;
    }
  }, []);

  const seedData = useCallback(async () => {
    try {
      const res = await axios.post(`${API}/seed`);
      setSeeded(true);
      return res.data;
    } catch (e) {
      console.error('Seed error:', e);
      return null;
    }
  }, []);

  // Single-call bootstrap: fetches user + features + permissions + business-lines
  // + notifications + feature-flags in ONE request instead of 6 separate calls.
  const bootstrapFromToken = useCallback(async (token) => {
    try {
      const res = await axios.get(`${API}/auth/bootstrap`, { params: { token }, timeout: 15000 });
      const data = res.data;
      // Set user
      setCurrentUser(data.user);
      // Daily panel-password re-verification gate (non-admins only)
      setReverifyRequired(!!data.reverify_required);
      // Set features
      const features = data.features || [];
      setRoleFeatures(features.length > 0 ? features : ['dashboard']);
      // Department-based section access (layered RBAC)
      setDepartmentFeatures(data.department_features || []);
      // Set sub-user permissions (empty for normal users)
      setSubUserPermissions(data.sub_user_permissions || {});
      // Set permissions
      setRolePermissions(data.permissions || {});
      // Set business lines (RBAC-filtered)
      const allLines = data.business_lines || [];
      setAllBusinessLines(allLines);
      const userBizIds = data.user?.assigned_businesses || [];
      const isAdmin = ['admin', 'business_head', 'sub_admin'].includes(data.user?.role);
      const _role = (data.user?.role || '').toLowerCase();
      const isCoordinator = _role.includes('coordinator') || (data.user?.mapped_manager_ids || []).length > 0;
      // Non-admins with NO assigned business line get NO business lines
      // (operations-only) — EXCEPT coordinators, who inherit all lines via
      // their mapped managers. Admins always get all.
      const lines = (isAdmin || (isCoordinator && userBizIds.length === 0))
        ? allLines
        : (userBizIds.length === 0 ? [] : allLines.filter(l => userBizIds.includes(l.id)));
      setBusinessLines(lines);
      if (lines.length > 0) {
        setSelectedBusiness(prev => prev || lines[0].id);
      }
      // Feature → business-line link map (for BL gating in sidebar + mapper).
      axios.get(`${API}/business-line-feature-map`).then(r => setBlFeatureMap(r.data?.map || {})).catch(() => {});
      // Sales-department config (departments treated as Sales + linked functions).
      axios.get(`${API}/sales-department-config`).then(r => setSalesDeptConfig(r.data || { departments: [], linked_functions: [] })).catch(() => {});
      // Set notifications
      const notifs = data.notifications || [];
      setNotifications(Array.isArray(notifs) ? notifs : (notifs.data || []));
      // Set feature flags
      if (data.feature_flags) {
        setFeatureFlags(f => ({ ...f, ...data.feature_flags }));
      }
      setBootstrapped(true);
      return data.user;
    } catch (e) {
      console.error('Bootstrap error:', e);
      return null;
    }
  }, []);

  const fetchNotifications = useCallback(async (userId) => {
    try {
      const res = await axios.get(`${API}/notifications`, { params: { user_id: userId } });
      const d = res.data;
      const list = d.data || d;
      setNotifications(list);

      // Surface a toast for any unread travel_plan_approval notifications we
      // haven't toasted in this browser session yet — proactive heads-up.
      try {
        const seenKey = `notif_toasted_${userId}`;
        const seen = new Set(JSON.parse(sessionStorage.getItem(seenKey) || '[]'));
        const cutoff = Date.now() - 24 * 60 * 60 * 1000;
        const fresh = (list || []).filter(n =>
          !n.read && n.notif_type === 'travel_plan_approval'
          && !seen.has(n.id)
          && (n.created_at ? new Date(n.created_at).getTime() >= cutoff : true)
        );
        if (fresh.length) {
          const { toast } = await import('sonner');
          fresh.slice(0, 3).forEach(n => {
            const isReject = (n.message || '').toLowerCase().includes('rejected');
            if (isReject) toast.error(n.message, { duration: 8000 });
            else toast.success(n.message, { duration: 8000 });
            seen.add(n.id);
          });
          sessionStorage.setItem(seenKey, JSON.stringify(Array.from(seen)));
        }
      } catch (_) {
        /* non-fatal */
      }

      // Ring the bell + heads-up when a NEW lead notification lands (claimable /
      // assigned / new-for-assigner / external API). Only for recent items (<10 min,
      // covers background-tab poll throttling) so we don't blast old unread ones.
      try {
        const leadTypes = ['lead_claimable', 'lead_assigned', 'lead_new', 'lead'];
        const chimeKey = `notif_chimed_${userId}`;
        const chimed = new Set(JSON.parse(sessionStorage.getItem(chimeKey) || '[]'));
        const recent = Date.now() - 10 * 60 * 1000;
        const freshLeads = (list || []).filter(n =>
          !n.read && leadTypes.includes(n.notif_type) && !chimed.has(n.id)
          && n.created_at && new Date(n.created_at).getTime() >= recent
        );
        if (freshLeads.length) {
          const { playBell, installUnlock } = await import('../lib/chime');
          installUnlock();
          const rang = playBell();
          const { toast } = await import('sonner');
          const extra = freshLeads.length > 1 ? ` (+${freshLeads.length - 1} more)` : '';
          toast.success(freshLeads[0].message + extra, {
            duration: 9000,
            description: rang ? undefined : 'Click anywhere to hear lead alerts.',
          });
          freshLeads.forEach(n => chimed.add(n.id));
          sessionStorage.setItem(chimeKey, JSON.stringify(Array.from(chimed).slice(-200)));
        }
      } catch (_) {
        /* non-fatal */
      }
    } catch (e) {
      console.error('Notifications error:', e);
    }
  }, []);

  const markNotifRead = useCallback(async (notifId) => {
    try {
      await axios.put(`${API}/notifications/${notifId}/read`);
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
    } catch (e) {
      console.error(e);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, null, { params: { user_id: currentUser?.id } });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (e) {
      console.error(e);
    }
  }, [currentUser]);

  // Pre-fetch (and refresh) all business places the current user can see, with
  // resilient retries. Exposed as `refreshBusinessPlaces` so any component can
  // force a re-fetch on demand. Auto-runs after login.
  const refreshBusinessPlaces = useCallback(async () => {
    if (!currentUser?.id) return;
    setAllPlacesLoading(true);
    setAllPlacesError(null);
    let attempts = 0;
    const tryFetch = async () => {
      attempts += 1;
      try {
        const r = await axios.get(`${API}/business-places`, {
          params: { user_id: currentUser.id, limit: 2000, approval_status: 'approved' },
          timeout: 15000,
        });
        setAllPlaces(r.data?.data || r.data || []);
        setAllPlacesLoadedAt(Date.now());
        setAllPlacesLoading(false);
      } catch (e) {
        if (attempts < 3) {
          setTimeout(tryFetch, 1500 * attempts);
        } else {
          setAllPlacesError(e.response?.data?.detail || 'Failed to load business places');
          setAllPlacesLoading(false);
        }
      }
    };
    await tryFetch();
  }, [currentUser?.id]);

  useEffect(() => {
    if (currentUser?.id) {
      // Don't auto-fetch business places on login — defer to lazy load.
      // Only clear stale data from previous user to prevent RBAC leakage.
      setAllPlaces([]);
      setAllPlacesLoadedAt(null);
      setAllPlacesError(null);
    } else {
      // Logout — clear cache entirely.
      setAllPlaces([]);
      setAllPlacesLoadedAt(null);
      setAllPlacesError(null);
      setAllPlacesLoading(false);
    }
  }, [currentUser?.id]);

  useEffect(() => {
    // Poll notifications so the chime + unread badge update live (e.g. when a
    // lead becomes claimable or is assigned while the app is open).
    if (!currentUser?.id) return;
    const t = setInterval(() => fetchNotifications(currentUser.id), 25000);
    return () => clearInterval(t);
  }, [currentUser?.id, fetchNotifications]);

  useEffect(() => {
    // Service worker relays incoming web pushes while the app is open → ring the
    // lead bell instantly and refresh the list (no wait for the 25s poll).
    if (!currentUser?.id || !('serviceWorker' in navigator)) return;
    const onMsg = (ev) => {
      const d = ev.data || {};
      if (d.type !== 'push-received') return;
      if (d.kind === 'lead') import('../lib/chime').then(({ playBell, installUnlock }) => { installUnlock(); playBell(); }).catch(() => {});
      fetchNotifications(currentUser.id);
    };
    navigator.serviceWorker.addEventListener('message', onMsg);
    return () => navigator.serviceWorker.removeEventListener('message', onMsg);
  }, [currentUser?.id, fetchNotifications]);

  useEffect(() => {
    // Register for browser/device web push once logged in. Prompts for
    // permission on first login (best-effort); silently re-subscribes after.
    if (currentUser?.id) {
      import('../lib/webPush').then(({ enablePush }) => {
        const key = `push_prompted_${currentUser.id}`;
        const prompt = !localStorage.getItem(key);
        enablePush(currentUser.id, { prompt }).finally(() => {
          if (prompt) localStorage.setItem(key, '1');
        });
      }).catch(() => {});
    }
  }, [currentUser?.id]);

  useEffect(() => {
    // Skip if bootstrapped — all data was already loaded in a single call
    if (bootstrapped) return;
    if (currentUser?.id) {
      fetchNotifications(currentUser.id);
    }
    if (currentUser?.is_sub_user) {
      // Sub-users are limited to the functionalities their parent granted —
      // NEVER the full role feature set. Derive from sub_user_permissions.
      const raw = currentUser.sub_user_permissions || {};
      const feats = Object.entries(raw).filter(([, v]) => v === 'view' || v === 'edit').map(([k]) => k);
      setRoleFeatures(feats.length ? feats : ['dashboard']);
      setRolePermissions(Object.fromEntries(
        Object.entries(raw).filter(([, v]) => v === 'view' || v === 'edit')
          .map(([k, v]) => [k, { view: true, edit: v === 'edit' }])
      ));
    } else if (currentUser?.role) {
      axios.get(`${API}/roles/${currentUser.role}/features`).then(res => {
        const features = res.data.features || [];
        if (features.length === 0) {
          console.warn(`[AppContext] Role '${currentUser.role}' returned empty features — defaulting to dashboard`);
          setRoleFeatures(['dashboard']);
        } else {
          setRoleFeatures(features);
        }
      }).catch((err) => {
        console.error(`[AppContext] Failed to load features for role '${currentUser.role}':`, err?.response?.status, err?.message);
        setRoleFeatures(['dashboard']);
      });
      axios.get(`${API}/roles/${currentUser.role}/permissions`).then(res => {
        setRolePermissions(res.data.permissions || {});
      }).catch(() => setRolePermissions({}));
    }
    if (currentUser?.department) {
      axios.get(`${API}/my-department-features`, { params: { department: currentUser.department } })
        .then(res => setDepartmentFeatures(res.data.features || []))
        .catch(() => setDepartmentFeatures([]));
    } else if (currentUser) {
      setDepartmentFeatures([]);
    }
    if (currentUser) {
      axios.get(`${API}/business-lines`).then(res => {
        const allLines = res.data || [];
        setAllBusinessLines(allLines);
        const userBizIds = currentUser.assigned_businesses || [];
        const isAdmin = ['admin', 'business_head', 'sub_admin'].includes(currentUser.role);
        const _role2 = (currentUser.role || '').toLowerCase();
        const isCoordinator = _role2.includes('coordinator') || (currentUser.mapped_manager_ids || []).length > 0;
        // Non-admins with NO assigned business line get NO business lines
        // (operations-only) — EXCEPT coordinators (inherit via mapped managers).
        const lines = (isAdmin || (isCoordinator && userBizIds.length === 0))
          ? allLines
          : (userBizIds.length === 0 ? [] : allLines.filter(l => userBizIds.includes(l.id)));
        setBusinessLines(lines);
        if (lines.length > 0 && !selectedBusiness) {
          setSelectedBusiness(lines[0].id);
        }
      }).catch(() => setBusinessLines([]));
      // Business-line → feature link map (for BL gating in sidebar + mapper).
      // Must load on FRESH login too, not only on token-bootstrap/reload.
      axios.get(`${API}/business-line-feature-map`).then(r => setBlFeatureMap(r.data?.map || {})).catch(() => {});
      axios.get(`${API}/sales-department-config`).then(r => setSalesDeptConfig(r.data || { departments: [], linked_functions: [] })).catch(() => {});
    }
    // Depend on primitive id/role instead of currentUser object identity so
    // this effect doesn't refire when other unrelated user fields change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, currentUser?.role, bootstrapped]);

  // Memoize the `api` object so its identity is stable across renders.
  // Without this, every render creates a new `api` reference, which forces
  // every child that captures `api` in an effect dep array to re-run.
  const api = useMemo(() => ({
    // Login endpoints: maxRetries=3 with FAST first-retry inside resilientPost
    // (250 ms). This makes the typical Cloudflare-520 transient invisible to
    // the user (~250 ms invisible recover) while still capping the worst-case
    // outage budget at ~3 attempts. LoginPage no longer wraps a separate
    // 5-attempt retry loop on top — the inner resilient retries cover it.
    login: (email) => resilientPost(`${API}/auth/login`, { email }, { timeout: 8000, maxRetries: 1 }),
    loginUser: (email, mobile) => resilientPost(`${API}/auth/login`, { email, mobile }, { timeout: 8000, maxRetries: 1 }),
    validateSession: (token) => resilientGet(`${API}/auth/validate-session`, { token }, { timeout: 8000, maxRetries: 2 }),
    loginWithPassword: (email, password) => resilientPost(`${API}/auth/login/password`, { email, password }, { timeout: 10000, maxRetries: 1 }),
    loginWithHiteam: (email, password) => resilientPost(`${API}/auth/login/hiteam`, { email, password }, { timeout: 15000, maxRetries: 3 }),
    requestLoginOtp: (email, mobile) => resilientPost(`${API}/auth/login/request-otp`, { email, mobile }, { timeout: 30000, maxRetries: 1 }),
    verifyLoginOtp: (email, otp) => resilientPost(`${API}/auth/login/verify-otp`, { email, otp }, { timeout: 15000, maxRetries: 3 }),
    requestLoginEmailOtp: (email) => resilientPost(`${API}/auth/login/request-email-otp`, { email }, { timeout: 20000, maxRetries: 1 }),
    verifyLoginEmailOtp: (email, otp) => resilientPost(`${API}/auth/login/verify-email-otp`, { email, otp }, { timeout: 15000, maxRetries: 2 }),
    setUserPassword: (userId, password) => axios.put(`${API}/auth/set-password/${userId}`, { password }),
    reverifyStatus: (token) => axios.get(`${API}/auth/reverify-status`, { params: { token } }),
    reverifyPassword: (token, password, method = 'panel') => axios.post(`${API}/auth/reverify-password`, { token, password, method }),
    reverifySetPassword: (token, password) => axios.post(`${API}/auth/reverify-set-password`, { token, password }),
    impersonateUser: (targetUserId) => axios.post(`${API}/auth/impersonate`, { admin_token: localStorage.getItem('session_token'), target_user_id: targetUserId }),
    getUsers: (params) => cachedAxiosGet(`${API}/users`, { params: { ...params, requester_id: currentUser?.id } }),
    getUser: (id) => cachedAxiosGet(`${API}/users/${id}`, { params: { requester_id: currentUser?.id } }),
    createUser: (data) => axios.post(`${API}/users`, data, { params: { requester_id: currentUser?.id } }),
    updateUser: (id, data) => axios.put(`${API}/users/${id}`, data, { params: { requester_id: currentUser?.id } }),
    deleteUser: (id) => axios.delete(`${API}/users/${id}`, { params: { requester_id: currentUser?.id } }),
    getHiteamCredentials: (userId) => cachedAxiosGet(`${API}/users/${userId}/hiteam-credentials`),
    saveHiteamCredentials: (userId, data) => axios.put(`${API}/users/${userId}/hiteam-credentials`, { ...data, requester_id: currentUser?.id }),
    getSubAdmins: () => cachedAxiosGet(`${API}/users/sub-admins`),
    promoteSubAdmin: (data) => axios.post(`${API}/users/promote-sub-admin`, data),
    demoteSubAdmin: (data) => axios.post(`${API}/users/demote-sub-admin`, data),

    // ── Attendance ──
    punchAttendance: (data) => axios.post(`${API}/attendance/punch`, data),
    getMyAttendanceToday: (userId) => axios.get(`${API}/attendance/today`, { params: { user_id: userId } }),
    getAttendance: (params) => axios.get(`${API}/attendance`, { params: { requester_id: currentUser?.id, ...params } }),
    getAttendanceRecord: (id) => axios.get(`${API}/attendance/${id}`),
    getAttendanceLive: () => axios.get(`${API}/attendance/live`, { params: { requester_id: currentUser?.id } }),
    approveAttendance: (id, data) => axios.put(`${API}/attendance/${id}/approve`, { approver_id: currentUser?.id, ...data }),
    setAttendanceSite: (userId, businessPlaceId) => axios.put(`${API}/attendance/site/${userId}`, { requester_id: currentUser?.id, business_place_id: businessPlaceId }),
    getAttendanceTeamMembers: () => axios.get(`${API}/attendance/team-members`, { params: { requester_id: currentUser?.id } }),
    getAttendanceRegister: (params) => axios.get(`${API}/attendance/register`, { params: { requester_id: currentUser?.id, ...params } }),
    attendanceExportUrl: (params) => {
      const qs = new URLSearchParams({ requester_id: currentUser?.id || '', ...params }).toString();
      return `${API}/attendance/export?${qs}`;
    },

    getBusinessPlaces: (params) => cachedAxiosGet(`${API}/business-places`, { params }),
    getBusinessPlace: (placeId) => cachedAxiosGet(`${API}/business-places/${placeId}`),
    getMyBusinessPlaceRequests: () => cachedAxiosGet(`${API}/business-places/my-requests`, { params: { user_id: currentUser?.id } }),
    createBusinessPlace: (data) => axios.post(`${API}/business-places`, data),
    updateBusinessPlace: (id, data) => axios.put(`${API}/business-places/${id}`, data),
    deleteBusinessPlace: (id) => axios.delete(`${API}/business-places/${id}?requester_id=${currentUser?.id || ''}`),
    bulkUploadBusinessPlaces: (formData, reqId, placeType, dryRun, skipInvalid) => axios.post(
      `${API}/business-places/bulk-upload?requester_id=${reqId}&place_type=${encodeURIComponent(placeType || 'office')}${dryRun ? '&dry_run=true' : ''}${skipInvalid ? '&skip_invalid=true' : ''}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 },
    ),
    bulkUploadBusinessPlacesRows: (payload) => axios.post(`${API}/business-places/bulk-upload-rows`, payload, { timeout: 120000 }),
    getLocationTypes: () => cachedAxiosGet(`${API}/location-types`),
    updateLocationTypes: (types) => axios.put(`${API}/location-types`, { types }),
    getPlaceMappings: (placeId) => cachedAxiosGet(`${API}/business-places/${placeId}/mappings`),
    assignUserToPlace: (placeId, userId) => axios.put(`${API}/business-places/${placeId}/assign-user`, { user_id: userId, requester_id: currentUser?.id }),
    assignUsersToPlace: (placeId, userIds) => axios.put(`${API}/business-places/${placeId}/assign-users`, { user_ids: userIds, requester_id: currentUser?.id }),
    assignUserToPlaces: (userId, placeIds) => axios.put(`${API}/users/${userId}/assign-places`, { place_ids: placeIds, requester_id: currentUser?.id }),
    unassignUserFromPlace: (placeId, userId) => axios.put(`${API}/business-places/${placeId}/unassign-user`, { user_id: userId, requester_id: currentUser?.id }),
    linkSalespointToPlace: (placeId, spId) => axios.put(`${API}/business-places/${placeId}/link-salespoint`, { sales_point_id: spId, requester_id: currentUser?.id }),
    unlinkSalespointFromPlace: (placeId, spId) => axios.put(`${API}/business-places/${placeId}/unlink-salespoint`, { sales_point_id: spId, requester_id: currentUser?.id }),

    getSalesPoints: (params) => cachedAxiosGet(`${API}/sales-points`, { params: { ...params, requester_id: currentUser?.id } }),
    createSalesPoint: (data) => axios.post(`${API}/sales-points`, data),
    updateSalesPoint: (id, data) => axios.put(`${API}/sales-points/${id}?requester_id=${currentUser?.id || ''}`, data),
    deleteSalesPoint: (id) => axios.delete(`${API}/sales-points/${id}?requester_id=${currentUser?.id || ''}`),

    getVisits: (params) => cachedAxiosGet(`${API}/visits`, { params }),
    getVisit: (id) => cachedAxiosGet(`${API}/visits/${id}`),
    createVisit: (data) => axios.post(`${API}/visits`, data),
    assignVisit: (data) => axios.post(`${API}/visits/assign`, data),
    getSubordinatesForAssignment: (userId) => cachedAxiosGet(`${API}/visits/subordinates?user_id=${userId}`),
    // Generic subordinate fetch for any UI that needs it (Coverage tab, etc.)
    getSubordinates: (userId) => cachedAxiosGet(`${API}/visits/subordinates?user_id=${userId}`),
    updateVisit: (id, data) => axios.put(`${API}/visits/${id}`, data),
    deleteVisit: (id, requesterId, reason) => axios.delete(`${API}/visits/${id}`, { params: { user_id: requesterId, reason: reason || '' } }),
    getDeletedVisits: (requesterId) => axios.get(`${API}/deleted-visits`, { params: { requester_id: requesterId } }),
    restoreVisit: (id, requesterId) => axios.post(`${API}/visits/${id}/restore`, null, { params: { user_id: requesterId } }),
    approveVisit: (id, action, approver_id, remarks) => axios.put(`${API}/visits/${id}/approve?action=${action}&approver_id=${approver_id}&remarks=${encodeURIComponent(remarks || '')}`),

    getDashboardStats: (params) => cachedAxiosGet(`${API}/dashboard/stats`, { params }),
    getPerformanceAnalytics: (params) => cachedAxiosGet(`${API}/performance/analytics`, { params }),
    getRevenueReport: (params) => cachedAxiosGet(`${API}/revenue/report`, { params: { ...params, requester_id: currentUser?.id }, timeout: 180000 }),
    getRevenueTrends: (params) => cachedAxiosGet(`${API}/revenue/trends`, { params: { ...params, requester_id: currentUser?.id }, timeout: 55000 }),
    startAsyncTrendsLoad: (params) => axios.post(`${API}/revenue/trends/async-load`, null, { params: { ...params, requester_id: currentUser?.id }, timeout: 15000 }),
    getTrendsTaskStatus: (taskId) => cachedAxiosGet(`${API}/revenue/trends/task/${taskId}`, { timeout: 15000 }),
    startAsyncReportLoad: (params) => axios.post(`${API}/revenue/report/async-load`, null, { params: { ...params, requester_id: currentUser?.id }, timeout: 15000 }),
    getReportTaskStatus: (taskId) => cachedAxiosGet(`${API}/revenue/report/task/${taskId}`, { timeout: 15000 }),
    getRevenueSummary: () => cachedAxiosGet(`${API}/revenue/summary`),
    getRevenueSyncStatus: () => cachedAxiosGet(`${API}/revenue/sync-status`),
    getPosCacheStatus: () => cachedAxiosGet(`${API}/revenue/pos-cache-status`),
    warmPosCache: () => axios.post(`${API}/revenue/warm-pos-cache`),
    getMasterCacheStatus: () => cachedAxiosGet(`${API}/revenue/master-cache-status`),
    refreshUserMapping: (email) => axios.post(`${API}/revenue/cache/refresh-user-mapping`,
      null,
      { params: { email, requester_id: currentUser?.id }, timeout: 60000 }),
    scanStaleMappings: (sample_size = 25) => axios.post(`${API}/revenue/cache/scan-stale-mappings`,
      null, { params: { requester_id: currentUser?.id, sample_size } }),
    listStaleMappingAlerts: (status = 'open') => cachedAxiosGet(`${API}/revenue/cache/stale-mapping-alerts`,
      { params: { requester_id: currentUser?.id, status } }),
    dismissStaleMappingAlert: (email) => axios.post(`${API}/revenue/cache/stale-mapping-alerts/dismiss`,
      null, { params: { requester_id: currentUser?.id, email } }),
    bulkApplyStaleAlerts: () => axios.post(`${API}/revenue/cache/stale-mapping-alerts/bulk-apply`,
      null, { params: { requester_id: currentUser?.id } }),
    getBulkApplyStaleStatus: () => cachedAxiosGet(`${API}/revenue/cache/stale-mapping-alerts/bulk-apply-status`,
      { params: { requester_id: currentUser?.id } }),
    cacheRefreshReportingMap: () => axios.post(`${API}/revenue/cache/refresh-reporting-map`,
      null, { params: { requester_id: currentUser?.id } }),
    getCacheReportingMapRefreshStatus: () => cachedAxiosGet(`${API}/revenue/cache/refresh-reporting-map-status`),
    triggerMasterCacheRefresh: (forceFull = false) => axios.post(`${API}/revenue/master-cache-refresh?force_full=${forceFull}`),
    getOnboardDetails: (preonboardId) => cachedAxiosGet(`${API}/revenue/onboard-details/${preonboardId}`, { params: { requester_id: currentUser?.id }, timeout: 30000 }),
    // Admin/BH cache-control endpoints (manual build flow)
    cacheStartInitialBuild: () => axios.post(`${API}/revenue/cache/start-initial-build`, null, { params: { requester_id: currentUser?.id } }),
    cacheRefreshCurrentMonth: () => axios.post(`${API}/revenue/cache/refresh-current-month`, null, { params: { requester_id: currentUser?.id } }),
    refreshYesterdayRevenue: () => axios.post(`${API}/revenue/refresh-yesterday`, null, { params: { requester_id: currentUser?.id } }),
    getRefreshYesterdayStatus: () => axios.get(`${API}/revenue/refresh-yesterday/status`, { params: { requester_id: currentUser?.id } }),
    retagProcessTypes: (from_date, to_date) => axios.post(`${API}/revenue/cache/retag-process-types`, null, { params: { from_date, to_date, requester_id: currentUser?.id } }),
    getRetagProcessTypesStatus: () => axios.get(`${API}/revenue/cache/retag-process-types/status`, { params: { requester_id: currentUser?.id } }),
    retagFull: () => axios.post(`${API}/revenue/cache/retag-full`, null, { params: { requester_id: currentUser?.id } }),
    getCacheConcurrency: () => axios.get(`${API}/revenue/cache/concurrency`, { params: { requester_id: currentUser?.id } }),
    setCacheConcurrency: (value) => axios.put(`${API}/revenue/cache/concurrency`, { value }, { params: { requester_id: currentUser?.id } }),
    getAutoRefreshInterval: () => axios.get(`${API}/revenue/cache/auto-refresh/interval`, { params: { requester_id: currentUser?.id } }),
    getAutoRefreshLoopStatus: () => axios.get(`${API}/revenue/cache/auto-refresh/loop-status`, { params: { requester_id: currentUser?.id } }),
    stopCurrentCacheLoad: () => axios.post(`${API}/revenue/cache/stop-current-load`, {}, { params: { requester_id: currentUser?.id } }),
    setAutoRefreshInterval: (value) => axios.put(`${API}/revenue/cache/auto-refresh/interval`, { value }, { params: { requester_id: currentUser?.id } }),
    setAutoRefreshPageConcurrency: (value) => axios.put(`${API}/revenue/cache/auto-refresh/page-concurrency`, { value }, { params: { requester_id: currentUser?.id } }),
    getAutoRefreshStatus: () => axios.get(`${API}/revenue/cache/auto-refresh/status`, { params: { requester_id: currentUser?.id } }),
    pauseAutoRefresh: () => axios.post(`${API}/revenue/cache/auto-refresh/pause`, null, { params: { requester_id: currentUser?.id } }),
    resumeAutoRefresh: () => axios.post(`${API}/revenue/cache/auto-refresh/resume`, null, { params: { requester_id: currentUser?.id } }),
    startLoadQueue: (chunks) => axios.post(`${API}/revenue/cache/load-queue`, { chunks }, { params: { requester_id: currentUser?.id } }),
    getLoadQueueStatus: () => axios.get(`${API}/revenue/cache/load-queue/status`, { params: { requester_id: currentUser?.id } }),
    stopLoadQueue: () => axios.post(`${API}/revenue/cache/load-queue/stop`, null, { params: { requester_id: currentUser?.id } }),
    getCacheOpQueue: () => axios.get(`${API}/revenue/cache/op-queue`, { params: { requester_id: currentUser?.id } }),
    clearCacheOpQueue: () => axios.post(`${API}/revenue/cache/op-queue/clear`, null, { params: { requester_id: currentUser?.id } }),
    getScrutinyStatus: () => axios.get(`${API}/revenue/scrutiny-scan/status`, { params: { requester_id: currentUser?.id } }),
    runScrutinyScan: (days = 0, max_fills = 0) => axios.post(`${API}/revenue/scrutiny-scan`, null, { params: { requester_id: currentUser?.id, days, max_fills } }),
    cacheBackfillHistory: () => axios.post(`${API}/revenue/cache/backfill-history`, null, { params: { requester_id: currentUser?.id } }),
    cacheFullRebuild: () => axios.post(`${API}/revenue/cache/full-rebuild`, null, { params: { requester_id: currentUser?.id } }),
    cacheRetryFailedChunks: () => axios.post(`${API}/revenue/cache/retry-failed-chunks`, null, { params: { requester_id: currentUser?.id } }),
    getDataHealth: (force = false) => cachedAxiosGet(`${API}/admin/data-health`, { params: { requester_id: currentUser?.id, force }, timeout: 60000 }),
    getDataHealthSummary: () => cachedAxiosGet(`${API}/admin/data-health/summary`, { params: { requester_id: currentUser?.id }, timeout: 10000 }),
    // Business Place coverage (My / Team)
    getCoverageSummary: (params = {}) => cachedAxiosGet(`${API}/business-places/coverage/summary`, { params: { requester_id: currentUser?.id, ...params } }),
    getPlaceCoverage: (placeId, params = {}) => cachedAxiosGet(`${API}/business-places/${placeId}/coverage`, { params: { requester_id: currentUser?.id, ...params } }),
    // Travel Plan: Clean Travel sign-off (BH/ZH/Admin)
    markTravelClean: (planId, remarks = '') => axios.put(`${API}/travel-plans/${planId}/mark-clean`, null, { params: { requester_id: currentUser?.id, remarks } }),
    // Deployment admin: status / runbook / plans
    getDeploymentStatus: () => cachedAxiosGet(`${API}/admin/deployment/status`, { params: { requester_id: currentUser?.id }, timeout: 30000 }),
    getDeploymentRunbook: () => cachedAxiosGet(`${API}/admin/deployment/runbook`, { params: { requester_id: currentUser?.id } }),
    listDeploymentPlans: (params = {}) => cachedAxiosGet(`${API}/admin/deployment/plans`, { params: { requester_id: currentUser?.id, ...params } }),
    getDeploymentPlan: (id) => cachedAxiosGet(`${API}/admin/deployment/plans/${id}`, { params: { requester_id: currentUser?.id } }),
    createDeploymentPlan: (body) => axios.post(`${API}/admin/deployment/plans`, { ...body, requester_id: currentUser?.id }),
    updateDeploymentPlan: (id, body) => axios.put(`${API}/admin/deployment/plans/${id}`, body, { params: { requester_id: currentUser?.id } }),
    deleteDeploymentPlan: (id) => axios.delete(`${API}/admin/deployment/plans/${id}`, { params: { requester_id: currentUser?.id } }),
    toggleDeploymentStep: (planId, stepIndex, done) => axios.patch(`${API}/admin/deployment/plans/${planId}/steps/${stepIndex}`, { done, requester_id: currentUser?.id }),
    listDeploymentHistory: (limit = 50) => cachedAxiosGet(`${API}/admin/deployment/history`, { params: { requester_id: currentUser?.id, limit } }),
    annotateDeploymentHistory: (id, body) => axios.patch(`${API}/admin/deployment/history/${id}`, { ...body, requester_id: currentUser?.id }),
    deleteDeploymentHistory: (id) => axios.delete(`${API}/admin/deployment/history/${id}`, { params: { requester_id: currentUser?.id } }),
    getDeploymentRegression: () => cachedAxiosGet(`${API}/admin/deployment/regression`, { params: { requester_id: currentUser?.id }, timeout: 10000 }),
    // Migration (Emergent → AWS): plan + downloadable doc + DB export/import
    getMigrationPlan: () => cachedAxiosGet(`${API}/admin/migration/plan`, { params: { requester_id: currentUser?.id } }),
    // For document/export downloads we return a URL the UI can window.open() against
    migrationDocumentUrl: (format) => `${API}/admin/migration/document?requester_id=${currentUser?.id || ''}&format=${format}`,
    migrationDbExportUrl: () => `${API}/admin/migration/db/export?requester_id=${currentUser?.id || ''}`,
    migrationDbImportPreview: (file) => {
      const fd = new FormData();
      fd.append('requester_id', currentUser?.id || '');
      fd.append('file', file);
      return axios.post(`${API}/admin/migration/db/import-preview`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
    },
    migrationDbImport: (file, defaultMode, perCollectionModes) => {
      const fd = new FormData();
      fd.append('requester_id', currentUser?.id || '');
      fd.append('default_mode', defaultMode);
      fd.append('per_collection_modes_json', JSON.stringify(perCollectionModes || {}));
      fd.append('file', file);
      return axios.post(`${API}/admin/migration/db/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 });
    },
    traceBarcode: (barcode) => axios.post(`${API}/inventory/trace-barcode`, { barcode, requester_id: currentUser?.id || '' }, { timeout: 30000 }),
    getInventoryRecentLookups: (limit = 25) => cachedAxiosGet(`${API}/inventory/recent-lookups`, { params: { requester_id: currentUser?.id, limit } }),
    bulkTraceFromText: (barcodes_text) => axios.post(`${API}/inventory/bulk-trace`, { barcodes_text, requester_id: currentUser?.id || '' }, { timeout: 30000 }),
    bulkTraceUpload: (file) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('requester_id', currentUser?.id || '');
      return axios.post(`${API}/inventory/bulk-trace/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
    },
    bulkTraceStatus: (jobId) => cachedAxiosGet(`${API}/inventory/bulk-trace/${jobId}`, { timeout: 30000 }),
    bulkTraceExportUrl: (jobId) => `${API}/inventory/bulk-trace/${jobId}/export`,
    bulkTraceListJobs: (limit = 50) => cachedAxiosGet(`${API}/inventory/bulk-trace/jobs/list`, { params: { requester_id: currentUser?.id, limit } }),
    bulkTraceDeleteJob: (jobId) => axios.delete(`${API}/inventory/bulk-trace/${jobId}`, { params: { requester_id: currentUser?.id } }),
    bulkTraceRerunJob: (jobId) => axios.post(`${API}/inventory/bulk-trace/${jobId}/rerun`, null, { params: { requester_id: currentUser?.id } }),
    bulkTraceCancelJob: (jobId) => axios.post(`${API}/inventory/bulk-trace/${jobId}/cancel`, null, { params: { requester_id: currentUser?.id } }),
    inventoryRevenueBarcodes: (limit = 500, issuer = '') => cachedAxiosGet(`${API}/inventory/revenue-barcodes`, { params: { limit, issuer, requester_id: currentUser?.id } }),
    inventoryVisibleUsers: () => cachedAxiosGet(`${API}/inventory/visible-users`, { params: { requester_id: currentUser?.id } }),
    inventoryValidateEmailMapping: (target_email) => axios.post(`${API}/inventory/validate-email-mapping`, { requester_id: currentUser?.id || '', target_email }, { timeout: 10000 }),
    inventoryView: (target_emails, tagstatuses) => axios.post(`${API}/inventory/view`, { requester_id: currentUser?.id || '', target_emails: Array.isArray(target_emails) ? target_emails : [target_emails].filter(Boolean), tagstatuses: Array.isArray(tagstatuses) ? tagstatuses.map(String) : [String(tagstatuses)].filter(Boolean) }, { timeout: 90000 }),
    inventoryTreeView: (target_email, tagstatus, page = 1, per_page = 100) => axios.post(`${API}/inventory/tree-view`, { requester_id: currentUser?.id || '', target_email: target_email || '', tagstatus: String(tagstatus || '1'), page, per_page }, { timeout: 30000 }),
    inventorySummary: (target_emails) => axios.post(`${API}/inventory/summary`, { requester_id: currentUser?.id || '', target_emails: Array.isArray(target_emails) ? target_emails : [target_emails].filter(Boolean) }, { timeout: 90000 }),
    inventoryViewExportUrl: () => `${API}/inventory/view/export`,
    cacheClear: () => axios.post(`${API}/revenue/cache/clear`, null, { params: { requester_id: currentUser?.id } }),
    cacheAuditLog: (limit = 20, skip = 0) => cachedAxiosGet(`${API}/revenue/cache/audit-log`, { params: { requester_id: currentUser?.id, limit, skip } }),
    cacheChunksPlan: () => cachedAxiosGet(`${API}/revenue/cache/chunks-plan`, { params: { requester_id: currentUser?.id } }),
    cacheDiagnostics: (probe_from = '', probe_to = '') => axios.get(`${API}/revenue/cache/diagnostics`, { params: { requester_id: currentUser?.id, probe_from, probe_to, samples: 8 }, timeout: 300000 }),
    cacheFetchChunk: (start_date, end_date) => axios.post(`${API}/revenue/cache/fetch-chunk`, null, { params: { start_date, end_date, requester_id: currentUser?.id }, timeout: 180000 }),
    cacheListS3Archives: () => axios.get(`${API}/revenue/cache/s3-archives`, { params: { requester_id: currentUser?.id }, timeout: 30000 }),
    cacheRestoreFromS3: (months = '') => axios.post(`${API}/revenue/cache/restore-from-s3`, null, { params: { requester_id: currentUser?.id, months }, timeout: 300000 }),
    cacheResetChunkLock: (clearRefreshing = false) => axios.post(`${API}/revenue/cache/reset-chunk-lock`, null, { params: { requester_id: currentUser?.id, clear_refreshing: clearRefreshing } }),
    cacheReresolveHierarchy: () => axios.post(`${API}/revenue/cache/reresolve-hierarchy`, null, { params: { requester_id: currentUser?.id }, timeout: 120000 }),
    cacheResolveAllUnassigned: () => axios.post(`${API}/revenue/cache/resolve-all-unassigned`, null, { params: { requester_id: currentUser?.id } }),
    cacheResolveAllStatus: () => cachedAxiosGet(`${API}/revenue/cache/resolve-all-status`, { params: { requester_id: currentUser?.id } }),
    getSystemMappedReport: (params = {}) => cachedAxiosGet(`${API}/revenue/system-mapped-report`, { params: { requester_id: currentUser?.id, ...params }, timeout: 120000 }),
    getSystemMappedRecords: (params = {}) => cachedAxiosGet(`${API}/revenue/system-mapped-records`, { params: { requester_id: currentUser?.id, ...params }, timeout: 120000 }),
    getSubordinateDashboard: (params) => cachedAxiosGet(`${API}/revenue/subordinate-dashboard`, { params, timeout: 180000 }),
    startAsyncDashboardLoad: (params) => axios.post(`${API}/revenue/subordinate-dashboard/async-load`, null, { params, timeout: 15000 }),
    getDashboardTaskStatus: (taskId) => cachedAxiosGet(`${API}/revenue/subordinate-dashboard/task/${taskId}`, { timeout: 15000 }),
    triggerRevenueSync: () => axios.post(`${API}/revenue/trigger-sync`),
    getReportingMap: (params) => cachedAxiosGet(`${API}/revenue/reporting-map`, { params }),
    collectReportingEmails: () => axios.post(`${API}/revenue/reporting-map/collect`),
    refreshReportingMap: (refreshAll = false) => axios.post(`${API}/revenue/reporting-map/refresh`, { refresh_all: refreshAll }, { timeout: 30000 }),
    getReportingMapRefreshStatus: () => cachedAxiosGet(`${API}/revenue/reporting-map/refresh-status`),
    refreshSingleReportingMap: (email) => axios.post(`${API}/revenue/reporting-map/refresh-single?email=${encodeURIComponent(email)}`),
    refreshBulkReportingMap: (body) => axios.post(`${API}/revenue/reporting-map/refresh-bulk`, body),

    getUserTree: () => cachedAxiosGet(`${API}/users/tree`, { params: { requester_id: currentUser?.id } }),

    // Target Tracker
    createTarget: (data) => axios.post(`${API}/targets`, { ...data, requester_id: currentUser?.id }),
    listTargets: (params) => cachedAxiosGet(`${API}/targets`, { params: { requester_id: currentUser?.id, ...params } }),
    getTargetProgress: (params) => cachedAxiosGet(`${API}/targets/progress`, { params: { requester_id: currentUser?.id, ...params } }),
    deleteTarget: (targetId) => axios.delete(`${API}/targets/${targetId}`, { params: { requester_id: currentUser?.id } }),

    // Margin Management
    marginProducts: (params) => cachedAxiosGet(`${API}/margin/products`, { params: { requester_id: currentUser?.id, ...params } }),
    marginCreateProduct: (data) => axios.post(`${API}/margin/products`, { ...data, requester_id: currentUser?.id }),
    marginUpdateProduct: (id, data) => axios.put(`${API}/margin/products/${id}`, { ...data, requester_id: currentUser?.id }),
    marginDeleteProduct: (id) => axios.delete(`${API}/margin/products/${id}`, { params: { requester_id: currentUser?.id } }),
    marginAutoDetect: () => cachedAxiosGet(`${API}/margin/products/auto-detect`, { params: { requester_id: currentUser?.id } }),
    marginGrossRates: (params) => cachedAxiosGet(`${API}/margin/gross-rates`, { params: { requester_id: currentUser?.id, ...params } }),
    marginCreateGrossRate: (data) => axios.post(`${API}/margin/gross-rates`, { ...data, requester_id: currentUser?.id }),
    marginDistributions: (params) => cachedAxiosGet(`${API}/margin/distributions`, { params: { requester_id: currentUser?.id, ...params } }),
    marginCreateDistribution: (data) => axios.post(`${API}/margin/distributions`, { ...data, requester_id: currentUser?.id }),
    marginApprovals: (params) => axios.get(`${API}/margin/approvals`, { params: { requester_id: currentUser?.id, ...params } }),
    marginProcessApproval: (data) => axios.post(`${API}/margin/approvals`, { ...data, approver_id: currentUser?.id }),
    marginBulkApproval: (data) => axios.post(`${API}/margin/approvals/bulk`, { ...data, approver_id: currentUser?.id }),
    marginInvalidated: () => cachedAxiosGet(`${API}/margin/invalidated`, { params: { requester_id: currentUser?.id } }),
    marginCalculator: (params) => cachedAxiosGet(`${API}/margin/calculator`, { params: { requester_id: currentUser?.id, ...params } }),
    marginDashboard: () => cachedAxiosGet(`${API}/margin/dashboard`, { params: { requester_id: currentUser?.id } }),
    marginUserProducts: (data) => axios.post(`${API}/margin/user-products`, { ...data, requester_id: currentUser?.id }),
    marginProductHistory: (data) => axios.post(`${API}/margin/product-history`, { ...data, requester_id: currentUser?.id }),
    marginProductCodeSuggestions: (q) => axios.get(`${API}/margin/product-code-suggestions`, { params: { q, requester_id: currentUser?.id } }),
    marginProductUserMapping: (data) => axios.post(`${API}/margin/product-user-mapping`, { ...data, requester_id: currentUser?.id }),
    salesPointsTeamView: (data) => axios.post(`${API}/sales-points/team-member-view`, { ...data, requester_id: currentUser?.id }),
    payoutShareWithBH: (cycleId) => axios.post(`${API}/payout/cycles/${cycleId}/share-data-with-bh`, { requester_id: currentUser?.id }),
    payoutOmitTreeRoots: (cycleId) => axios.get(`${API}/payout/cycles/${cycleId}/snapshots/omit-tree-roots`, { params: { requester_id: currentUser?.id } }),
    payoutOmitTreeBranch: (cycleId, masterEmail) => axios.post(`${API}/payout/cycles/${cycleId}/snapshots/omit-tree-branch`, { master_email: masterEmail, requester_id: currentUser?.id }),

    // Payout Management
    payoutCycles: () => cachedAxiosGet(`${API}/payout/cycles`, { params: { requester_id: currentUser?.id } }),
    payoutCreateCycle: (data) => axios.post(`${API}/payout/cycles`, { ...data, requester_id: currentUser?.id }),
    payoutGetCycle: (id) => cachedAxiosGet(`${API}/payout/cycles/${id}`, { params: { requester_id: currentUser?.id } }),
    payoutFetchDay: (cycleId, trancheId, date) => axios.post(`${API}/payout/cycles/${cycleId}/tranches/${trancheId}/fetch-day?date=${date}&requester_id=${currentUser?.id}`),
    payoutFetchAll: (cycleId, trancheId) => axios.post(`${API}/payout/cycles/${cycleId}/tranches/${trancheId}/fetch-all?requester_id=${currentUser?.id}`),
    payoutSnapshot: (cycleId, trancheId) => cachedAxiosGet(`${API}/payout/cycles/${cycleId}/snapshot`, { params: { requester_id: currentUser?.id, tranche_id: trancheId } }),
    payoutLockSnapshot: (data) => axios.post(`${API}/payout/cycles/${data.cycle_id}/lock`, { ...data, requester_id: currentUser?.id }),
    payoutDeductions: (cycleId) => cachedAxiosGet(`${API}/payout/deductions`, { params: { requester_id: currentUser?.id, cycle_id: cycleId } }),
    payoutCreateDeduction: (data) => axios.post(`${API}/payout/deductions`, { ...data, requester_id: currentUser?.id }),
    payoutUpdateDeductionStatus: (id, status, notes) => axios.patch(`${API}/payout/deductions/${id}/status?status=${status}&requester_id=${currentUser?.id}&notes=${notes || ''}`),
    payoutResolveOwner: (identifier, type) => axios.post(`${API}/payout/deductions/resolve-owner?identifier=${encodeURIComponent(identifier)}&identifier_type=${type || ''}`),
    payoutMargins: (cycleId, trancheId) => cachedAxiosGet(`${API}/payout/margins`, { params: { requester_id: currentUser?.id, cycle_id: cycleId, tranche_id: trancheId } }),
    payoutSaveMargin: (data) => axios.post(`${API}/payout/margins`, { ...data, requester_id: currentUser?.id }),
    payoutCopyMargins: (cycleId, source, targets) => axios.post(`${API}/payout/margins/copy-tranche?cycle_id=${cycleId}&source_tranche=${source}&target_tranches=${targets}&requester_id=${currentUser?.id}`),
    payoutAudit: (cycleId) => cachedAxiosGet(`${API}/payout/audit`, { params: { requester_id: currentUser?.id, cycle_id: cycleId } }),

    // HR Dashboard
    hrKpiScores: (params) => cachedAxiosGet(`${API}/hr/kpi-scores`, { params: { requester_id: currentUser?.id, ...params } }),
    hrLeaderboard: (params) => cachedAxiosGet(`${API}/hr/leaderboard`, { params: { requester_id: currentUser?.id, ...params } }),
    hrPipConfig: () => cachedAxiosGet(`${API}/hr/pip/config`, { params: { requester_id: currentUser?.id } }),
    hrSetPipConfig: (data) => axios.post(`${API}/hr/pip/config`, { ...data, requester_id: currentUser?.id }),
    hrPipRecords: (params) => cachedAxiosGet(`${API}/hr/pip/records`, { params: { requester_id: currentUser?.id, ...params } }),
    hrCreatePip: (data) => axios.post(`${API}/hr/pip/records`, { ...data, requester_id: currentUser?.id }),
    hrUpdatePip: (id, data) => axios.put(`${API}/hr/pip/records/${id}`, { ...data, requester_id: currentUser?.id }),
    hrIncentiveStructures: (params) => cachedAxiosGet(`${API}/hr/incentives/structures`, { params: { requester_id: currentUser?.id, ...params } }),
    hrCreateIncentive: (data) => axios.post(`${API}/hr/incentives/structures`, { ...data, requester_id: currentUser?.id }),
    hrApproveIncentive: (data) => axios.post(`${API}/hr/incentives/approve`, { ...data, approver_id: currentUser?.id }),
    hrCalculateIncentives: (params) => cachedAxiosGet(`${API}/hr/incentives/calculate`, { params: { requester_id: currentUser?.id, ...params } }),

    // Order Inventory
    createInventoryOrder: (data) => axios.post(`${API}/inventory/orders`, { ...data, requester_id: currentUser?.id }),
    listInventoryOrders: (params) => cachedAxiosGet(`${API}/inventory/orders`, { params: { requester_id: currentUser?.id, ...params } }),
    getInventoryOrder: (orderId) => cachedAxiosGet(`${API}/inventory/orders/${orderId}`, { params: { requester_id: currentUser?.id } }),
    updateInventoryOrder: (orderId, data) => axios.put(`${API}/inventory/orders/${orderId}`, { ...data, requester_id: currentUser?.id }),
    deleteInventoryOrder: (orderId) => axios.delete(`${API}/inventory/orders/${orderId}`, { params: { requester_id: currentUser?.id } }),
    getInventoryOrderHistory: (params) => cachedAxiosGet(`${API}/inventory/order-history`, { params: { requester_id: currentUser?.id, ...params } }),

    // Inventory Eligibility Config
    getEligibilityConfig: () => cachedAxiosGet(`${API}/inventory/eligibility-config`),
    updateEligibilityConfig: (data) => axios.put(`${API}/inventory/eligibility-config`, { ...data, requester_id: currentUser?.id }),
    checkEligibility: (userEmail) => cachedAxiosGet(`${API}/inventory/eligibility-check`, { params: { user_email: userEmail, requester_id: currentUser?.id } }),

    requestBusinessPlace: (data) => axios.post(`${API}/business-places/request`, data),
    getApprovals: (params) => cachedAxiosGet(`${API}/approvals`, { params }),
    approveRequest: (id, data) => axios.put(`${API}/approvals/${id}/approve`, data),
    rejectRequest: (id, data) => axios.put(`${API}/approvals/${id}/reject`, data),
    getAllApprovals: (params) => axios.get(`${API}/approvals/all`, { params }),
    getApproval: (id) => cachedAxiosGet(`${API}/approvals/${id}`),

    saveImportConfig: (data, role) => axios.post(`${API}/import/config?requester_role=${role}`, data),
    getImportConfigs: (role) => cachedAxiosGet(`${API}/import/configs?requester_role=${role}`),
    fetchImportData: (data, role) => axios.post(`${API}/import/fetch?requester_role=${role}`, data),
    getImports: () => cachedAxiosGet(`${API}/imports`),
    getImportDetail: (id) => cachedAxiosGet(`${API}/imports/${id}`),

    getRoles: () => cachedAxiosGet(`${API}/roles`),
    getRoleFeatures: (roleName) => cachedAxiosGet(`${API}/roles/${roleName}/features`),
    createRole: (data) => axios.post(`${API}/roles`, data),
    updateRole: (id, data) => axios.put(`${API}/roles/${id}`, data),
    deleteRole: (id) => axios.delete(`${API}/roles/${id}`),
    getFeatures: () => cachedAxiosGet(`${API}/features`),
    getSubFeatures: () => cachedAxiosGet(`${API}/features/sub-features`),
    getRolePermissions: (roleName) => cachedAxiosGet(`${API}/roles/${roleName}/permissions`),
    updateRolePermissions: (roleName, permissions) => axios.put(`${API}/roles/${roleName}/permissions`, { permissions }),

    exportTeam: (zone) => `${API}/export/team${zone ? `?zone=${zone}` : ''}`,
    exportVisits: (zone, userId) => {
      const params = new URLSearchParams();
      if (zone) params.set('zone', zone);
      if (userId) params.set('user_id', userId);
      return `${API}/export/visits${params.toString() ? `?${params}` : ''}`;
    },
    exportSalesPoints: (zone, spType) => {
      const params = new URLSearchParams();
      if (zone) params.set('zone', zone);
      if (spType) params.set('sp_type', spType);
      return `${API}/export/sales-points${params.toString() ? `?${params}` : ''}`;
    },
    exportPerformance: (zone) => `${API}/export/performance${zone ? `?zone=${zone}` : ''}`,

    getBusinessLines: () => cachedAxiosGet(`${API}/business-lines`),
    createBusinessLine: (data) => axios.post(`${API}/business-lines`, data),
    updateBusinessLine: (id, data) => axios.put(`${API}/business-lines/${id}`, data),
    getCycleDates: (params) => cachedAxiosGet(`${API}/cycle-dates`, { params }),
    deleteBusinessLine: (id) => axios.delete(`${API}/business-lines/${id}`),

    getTravelPlans: (params) => cachedAxiosGet(`${API}/travel-plans`, { params }),
    getTravelPlan: (id) => cachedAxiosGet(`${API}/travel-plans/${id}`),
    createTravelPlan: (data, userId) => axios.post(`${API}/travel-plans?user_id=${userId}`, data),
    updateTravelPlan: (id, data) => axios.put(`${API}/travel-plans/${id}`, data),
    approveTravelPlan: (id, action, remarks, approver_id) => axios.put(`${API}/travel-plans/${id}/approve?action=${action}&remarks=${encodeURIComponent(remarks || '')}${approver_id ? `&approver_id=${encodeURIComponent(approver_id)}` : ''}`),
    startTravelPlan: (id, selfie, lat, lng, extra = {}) => axios.put(`${API}/travel-plans/${id}/start`, { selfie: selfie || '', lat: lat || 0, lng: lng || 0, ...extra }),
    checkinLocation: (id, idx, data) => axios.put(`${API}/travel-plans/${id}/checkin/${idx}`, data),
    checkoutLocation: (id, idx, data) => axios.put(`${API}/travel-plans/${id}/checkout/${idx}`, data),
    completeTravelPlan: (id) => axios.put(`${API}/travel-plans/${id}/complete`),
    uploadBills: (id, bills) => axios.put(`${API}/travel-plans/${id}/upload-bills`, bills),
    finalizeExpenses: (id) => axios.put(`${API}/travel-plans/${id}/finalize-expenses`),
    deleteTravelPlan: (id, requesterId, reason) => axios.delete(`${API}/travel-plans/${id}`, { params: { user_id: requesterId, reason: reason || '' } }),
    getDeletedTravelPlans: (requesterId) => axios.get(`${API}/deleted-travel-plans`, { params: { requester_id: requesterId } }),
    restoreTravelPlan: (id, requesterId) => axios.post(`${API}/travel-plans/${id}/restore`, null, { params: { user_id: requesterId } }),

    // Unplanned visits
    startUnplannedVisit: (userId, data) => axios.post(`${API}/travel-plans/unplanned/start?user_id=${userId}`, data),
    unplannedCheckin: (id, data) => axios.post(`${API}/travel-plans/${id}/unplanned/checkin`, data),
    unplannedCheckout: (id, data) => axios.post(`${API}/travel-plans/${id}/unplanned/checkout`, data),
    endUnplannedVisit: (id, data) => axios.post(`${API}/travel-plans/${id}/unplanned/end`, data),
    getActiveUnplannedVisit: (userId) => cachedAxiosGet(`${API}/travel-plans/unplanned/active?user_id=${userId}`),
    getPickableVisits: (userId, limit = 20) => cachedAxiosGet(`${API}/users/${userId}/visits/pickable`, { params: { limit } }),
    linkVisitToLead: (leadId, visitId) => axios.post(`${API}/leads/${leadId}/link-visit`, { visit_id: visitId }),
    unlinkVisitFromLead: (leadId, visitId) => axios.delete(`${API}/leads/${leadId}/link-visit/${visitId}`),

    // Leads
    getLeads: (params) => cachedAxiosGet(`${API}/leads`, { params }),
    getLeadCreators: () => cachedAxiosGet(`${API}/leads/creators`, { params: { user_id: currentUser?.id, role: currentUser?.role } }),
    getLead: (id) => cachedAxiosGet(`${API}/leads/${id}`),
    createLead: (data, userId) => axios.post(`${API}/leads?user_id=${userId}`, data),
    updateLead: (id, data) => axios.put(`${API}/leads/${id}`, data),
    addLeadRemark: (id, text, extra = {}) => axios.post(`${API}/leads/${id}/remarks`, { text, by: currentUser?.id, by_name: currentUser?.name, ...extra }),
    setLeadFollowUp: (id, data) => axios.post(`${API}/leads/${id}/follow-up`, { ...data, user_id: currentUser?.id }),
    clearLeadFollowUp: (id) => axios.delete(`${API}/leads/${id}/follow-up`),
    getUpcomingFollowUps: () => axios.get(`${API}/leads/follow-ups/upcoming`, { params: { user_id: currentUser?.id, role: currentUser?.role } }),
    reassignLeadBusinessLine: (id, businessId) => axios.put(`${API}/leads/${id}/business-line`, { requester_id: currentUser?.id, business_id: businessId }),
    getLeadDistribution: () => axios.get(`${API}/lead-distribution`, { params: { requester_id: currentUser?.id } }),
    getDistSourceSummary: (date) => axios.get(`${API}/lead-distribution/source-summary`, { params: { requester_id: currentUser?.id, ...(date ? { date } : {}) } }),
    backfillApiLeads: () => axios.post(`${API}/lead-distribution/backfill-api-leads`, { requester_id: currentUser?.id }),
    getDistAssigners: () => axios.get(`${API}/lead-distribution/assigners`, { params: { requester_id: currentUser?.id } }),
    assignDistLead: (leadId, assigneeId) => axios.post(`${API}/lead-distribution/${leadId}/assign`, { requester_id: currentUser?.id, assignee_id: assigneeId }),
    bulkAssignDistLeads: (leadIds, assigneeId) => axios.post(`${API}/lead-distribution/bulk-assign`, { requester_id: currentUser?.id, lead_ids: leadIds, assignee_id: assigneeId }),
    addDistLeadNote: (leadId, text, outcome) => axios.post(`${API}/lead-distribution/${leadId}/note`, { requester_id: currentUser?.id, text, outcome }),
    claimDistLead: (leadId) => axios.post(`${API}/lead-distribution/${leadId}/claim`, { requester_id: currentUser?.id }),
    getLeadTracking: (params = {}) => axios.get(`${API}/lead-tracking`, { params: { requester_id: currentUser?.id, ...params } }),
    getLeadTrackingDetail: (leadId) => axios.get(`${API}/lead-tracking/${leadId}`, { params: { requester_id: currentUser?.id } }),
    getLeadTrackingFilters: () => axios.get(`${API}/lead-tracking/filters`, { params: { requester_id: currentUser?.id } }),
    deleteLead: (id) => axios.delete(`${API}/leads/${id}`),
    requestLeadConversion: (id, data) => axios.post(`${API}/leads/${id}/request-conversion`, data),

    // Bulk User Upload
    bulkUploadUsers: (data) => axios.post(`${API}/users/bulk-upload`, { ...data, requester_id: currentUser?.id }),
    getBulkUploadTemplate: () => cachedAxiosGet(`${API}/users/bulk-upload/template`),
    bulkRoleUpdate: (data) => axios.put(`${API}/users/bulk-role-update`, { ...data, requester_id: currentUser?.id }),
    bulkAssignBusinesses: (data) => axios.put(`${API}/users/bulk-assign-businesses`, data),
    bulkRoleUpdateCsv: (data) => axios.post(`${API}/users/bulk-role-update-csv`, data),
    bulkDepartmentUpdate: (data) => axios.put(`${API}/users/bulk-department-update`, data),
    bulkFeatureOverrides: (data) => axios.put(`${API}/users/bulk-feature-overrides`, data),
    getRoleDeptMap: ()       => cachedAxiosGet(`${API}/users/role-department-map`, {}, 10 * 60 * 1000),
    setRoleDeptMapping: (data) => axios.put(`${API}/users/role-department-map`, data),
    getHiTeamMobile: (email) => axios.post(`${API}/users/hiteam-mobile`, { email }),
    resolveManagers: () => axios.post(`${API}/users/resolve-managers`),
    // Data Management
    requestCleanupOtp: (data) => axios.post(`${API}/data-management/request-cleanup-otp`, data),
    verifyCleanupOtp: (data) => axios.post(`${API}/data-management/verify-cleanup-otp`, data),
    cleanupData: (data) => axios.post(`${API}/data-management/cleanup`, data, { timeout: 60000 }),
    cleanupJobStatus: (jobId, requesterId) => cachedAxiosGet(`${API}/data-management/cleanup/status/${jobId}`, { params: { requester_id: requesterId }, timeout: 10000 }),
    cleanupJobs: (requesterId, limit = 10) => cachedAxiosGet(`${API}/data-management/cleanup/jobs`, { params: { requester_id: requesterId, limit }, timeout: 10000 }),
    getTrash: (params) => cachedAxiosGet(`${API}/data-management/trash`, { params }),
    getTrashSummary: (params) => cachedAxiosGet(`${API}/data-management/trash/summary`, { params }),
    recoverData: (data) => axios.post(`${API}/data-management/recover`, data),
    recoverAllData: (data) => axios.post(`${API}/data-management/recover-all`, data),
    getCollectionStats: (params) => cachedAxiosGet(`${API}/data-management/collection-stats`, { params }),

    // Potential Businesses
    getPotentialBusinesses: (params) => cachedAxiosGet(`${API}/potential-businesses`, { params }),
    createPotentialBusiness: (data) => axios.post(`${API}/potential-businesses`, data),
    updatePotentialBusiness: (id, data) => axios.put(`${API}/potential-businesses/${id}`, data),
    deletePotentialBusiness: (id) => axios.delete(`${API}/potential-businesses/${id}`),

    // OTP
    sendOtp: (mobile) => axios.post(`${API}/otp/send?mobile=${encodeURIComponent(mobile)}`),
    verifyOtp: (mobile, code) => axios.post(`${API}/otp/verify?mobile=${encodeURIComponent(mobile)}&code=${code}`),

    // OCR
    ocrVisitingCard: (image) => axios.post(`${API}/ocr/visiting-card`, { image }),

    // Settings
    getSetting: (key) => cachedAxiosGet(`${API}/settings/${key}`),
    updateSetting: (key, value) => axios.put(`${API}/settings/${key}`, { value }),

    // Expense Claims
    getExpenseClaims: (params) => cachedAxiosGet(`${API}/expense-claims`, { params }),
    getExpenseClaim: (id) => cachedAxiosGet(`${API}/expense-claims/${id}`),
    processExpenseClaim: (id, data) => axios.put(`${API}/expense-claims/${id}/action`, data),
    adjustAccountsClaimAmount: (id, adjusted_amount, adjustment_reason) =>
      axios.put(`${API}/accounts/expense-claims/${id}/adjust-amount`, {
        requester_id: currentUser?.id, adjusted_amount, adjustment_reason,
      }),
    getExpenseClaimStats: (params) => cachedAxiosGet(`${API}/expense-claims/stats/summary`, { params }),
    getExpenseAnalytics: (params) => cachedAxiosGet(`${API}/expense-claims/analytics/trends`, { params }),
    getDbInfo: () => cachedAxiosGet(`${API}/db-info`),
    getDatabaseStats: () => cachedAxiosGet(`${API}/export/database-stats`),

    // Expense Policies
    getExpensePolicies: (params) => cachedAxiosGet(`${API}/expense-policies`, { params }),
    upsertExpensePolicy: (data) => axios.post(`${API}/expense-policies`, data),
    deleteExpensePolicy: (id) => axios.delete(`${API}/expense-policies/${id}`),
    // Designations
    getDesignations: () => cachedAxiosGet(`${API}/designations`),
    createDesignation: (data) => axios.post(`${API}/designations`, data),
    updateDesignation: (id, data) => axios.put(`${API}/designations/${id}`, data),
    deleteDesignation: (id) => axios.delete(`${API}/designations/${id}`),
    getDepartments: () => axios.get(`${API}/departments`),
    createDepartment: (data) => axios.post(`${API}/departments`, data),
    updateDepartment: (id, data) => axios.put(`${API}/departments/${id}`, data),
    deleteDepartment: (id) => axios.delete(`${API}/departments/${id}`),
    getDepartmentAccess: (rid) => axios.get(`${API}/department-access`, { params: { requester_id: rid } }),
    setDepartmentAccess: (data) => axios.put(`${API}/department-access`, data),
    syncDeptCatalog: (data) => axios.post(`${API}/department-access/sync-catalog`, data),
    requestPermanentDeactivation: (userId, data) => axios.post(`${API}/users/${userId}/permanent-deactivation`, data),
    getDeactivationRequests: (rid, status) => axios.get(`${API}/deactivation-requests`, { params: { requester_id: rid, status } }),
    decideDeactivationRequest: (reqId, data) => axios.post(`${API}/deactivation-requests/${reqId}/decide`, data),
    getApiClients: (rid) => axios.get(`${API}/api-clients`, { params: { requester_id: rid } }),
    createApiClient: (data) => axios.post(`${API}/api-clients`, data),
    updateApiClient: (cid, data) => axios.patch(`${API}/api-clients/${cid}`, data),
    regenerateApiClientKey: (cid, data) => axios.post(`${API}/api-clients/${cid}/regenerate-key`, data),
    deleteApiClient: (cid, rid) => axios.delete(`${API}/api-clients/${cid}`, { params: { requester_id: rid } }),
    getApiClientLogs: (cid, rid) => axios.get(`${API}/api-clients/${cid}/logs`, { params: { requester_id: rid } }),
    getBranches: (rid, businessId) => axios.get(`${API}/branches`, { params: { requester_id: rid, business_id: businessId } }),
    getBranchCompliance: (rid, businessId) => axios.get(`${API}/branches/compliance`, { params: { requester_id: rid, business_id: businessId } }),
    getUserBranches: (userId) => axios.get(`${API}/branches/for-user/${userId}`, { params: { requester_id: currentUser?.id } }),
    setUserBranches: (userId, branchIds) => axios.put(`${API}/branches/for-user/${userId}`, { requester_id: currentUser?.id, branch_ids: branchIds }),
    bulkAddBranchMembers: (branchId, userIds) => axios.post(`${API}/branches/${branchId}/members/bulk-add`, { requester_id: currentUser?.id, user_ids: userIds }),
    createBranch: (data) => axios.post(`${API}/branches`, data),
    updateBranch: (bid, data) => axios.patch(`${API}/branches/${bid}`, data),
    deleteBranch: (bid, rid) => axios.delete(`${API}/branches/${bid}`, { params: { requester_id: rid } }),
    getBankAccounts: (rid) => axios.get(`${API}/bank-accounts`, { params: { requester_id: rid } }),
    getBranchOptions: (rid, businessId) => axios.get(`${API}/branch-options`, { params: { requester_id: rid, business_id: businessId } }),
    createBankAccount: (data) => axios.post(`${API}/bank-accounts`, data),
    updateBankAccount: (id, data) => axios.patch(`${API}/bank-accounts/${id}`, data),
    deleteBankAccount: (id, rid) => axios.delete(`${API}/bank-accounts/${id}`, { params: { requester_id: rid } }),
    myDepartmentFeatures: (department) => axios.get(`${API}/my-department-features`, { params: { department } }),
    getBusinessLineAccess: (rid) => axios.get(`${API}/business-line-access`, { params: { requester_id: rid } }),
    setBusinessLineAccess: (data) => axios.put(`${API}/business-line-access`, data),
    getBusinessLineFeatureMap: () => axios.get(`${API}/business-line-feature-map`),
    getSalesDepartmentConfig: () => axios.get(`${API}/sales-department-config`),
    setSalesDepartmentConfig: (data) => axios.put(`${API}/sales-department-config`, data),
    // Backfill baseline (idempotent)
    backfillBaseline: () => axios.post(`${API}/admin/backfill-baseline`),
    seedExpensePolicies: (data) => axios.post(`${API}/expense-policies/seed`, data),    exportExpensePolicies: (business_line_id) =>
      cachedAxiosGet(`${API}/expense-policies/export`, { params: { business_line_id }, responseType: 'blob' }),
    importExpensePolicies: (business_line_id, file) => {
      const fd = new FormData();
      fd.append('file', file);
      return axios.post(`${API}/expense-policies/import?business_line_id=${encodeURIComponent(business_line_id)}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    getExpenseCycles: (params) => cachedAxiosGet(`${API}/expense-cycles`, { params }),
    upsertExpenseCycle: (data) => axios.post(`${API}/expense-cycles`, data),

    // Travel Modes
    getTravelModes: (params) => cachedAxiosGet(`${API}/travel-modes`, { params }),
    createTravelMode: (data) => axios.post(`${API}/travel-modes`, data),
    updateTravelMode: (id, data) => axios.put(`${API}/travel-modes/${id}`, data),
    deleteTravelMode: (id) => axios.delete(`${API}/travel-modes/${id}`),

    // Fuel Rates
    getFuelRates: (params) => cachedAxiosGet(`${API}/fuel-rates`, { params }),
    upsertFuelRate: (data) => axios.post(`${API}/fuel-rates`, data),
    deleteFuelRate: (id) => axios.delete(`${API}/fuel-rates/${id}`),
    seedFuelRates: (data) => axios.post(`${API}/fuel-rates/seed`, data),
    exportFuelRates: (business_id) =>
      cachedAxiosGet(`${API}/fuel-rates/export`, { params: { business_id }, responseType: 'blob' }),
    importFuelRates: (business_id, file) => {
      const fd = new FormData();
      fd.append('file', file);
      return axios.post(`${API}/fuel-rates/import?business_id=${encodeURIComponent(business_id)}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },

    // Geolocation
    getGeoTolerances: (params) => cachedAxiosGet(`${API}/geo-tolerance`, { params }),
    upsertGeoTolerance: (data) => axios.post(`${API}/geo-tolerance`, data),
    validateLocation: (data) => axios.post(`${API}/validate-location`, data),

    // Google Maps
    getGoogleMapsKey: () => cachedAxiosGet(`${API}/google-maps-key`),
    getGoogleMapsKeyRaw: () => cachedAxiosGet(`${API}/google-maps-key/raw`),
    updateGoogleMapsKey: (value) => axios.put(`${API}/google-maps-key`, { value }),

    // Comparison
    compareRanges: (data) => axios.post(`${API}/comparison`, data),

    // Fuel Claims
    getFuelSuggestion: (planId) => cachedAxiosGet(`${API}/travel-plans/${planId}/fuel-suggestion`),
    submitFuelClaim: (planId, data) => axios.put(`${API}/travel-plans/${planId}/fuel-claim`, data),

    // OTP API Config
    getOtpApiConfig: () => cachedAxiosGet(`${API}/otp-api-config`),
    updateOtpApiConfig: (data) => axios.put(`${API}/otp-api-config`, data),
    testOtpApiConfig: (data) => axios.post(`${API}/otp-api-config/test`, data),

    // MSG91 Config
    getMsg91Config: () => cachedAxiosGet(`${API}/msg91-config`),
    updateMsg91Config: (data) => axios.put(`${API}/msg91-config`, data),
    testMsg91Config: (data) => axios.post(`${API}/msg91-config/test`, data),

    // Amazon SES Email Config
    getSesConfig: () => cachedAxiosGet(`${API}/ses-config`),
    updateSesConfig: (data) => axios.put(`${API}/ses-config`, data),
    testSesConfig: (data) => axios.post(`${API}/ses-config/test`, data),

    // OTP Provider
    getOtpProvider: () => cachedAxiosGet(`${API}/otp-provider`),
    updateOtpProvider: (data) => axios.put(`${API}/otp-provider`, data),
    getFeatureFlags: () => cachedAxiosGet(`${API}/feature-flags`),
    updateFeatureFlags: (data) => axios.put(`${API}/feature-flags`, data, { params: { requester_id: currentUser?.id } }),
  }), [currentUser?.id]);

  // Load feature flags once on mount — skip if already bootstrapped.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (bootstrapped) return;
    axios.get(`${API}/feature-flags`).then(res => {
      setFeatureFlags(f => ({ ...f, ...(res.data || {}) }));
    }).catch(() => { /* ignore — defaults stay in place */ });
  }, [bootstrapped]);

  const refreshFeatureFlags = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/feature-flags`);
      setFeatureFlags(f => ({ ...f, ...(res.data || {}) }));
      return res.data;
    } catch { return null; }
  }, []);

  const refreshSalesDeptConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/sales-department-config`);
      setSalesDeptConfig(res.data || { departments: [], linked_functions: [] });
      return res.data;
    } catch { return null; }
  }, []);

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'sub_admin';
  const userZone = currentUser?.zone && currentUser?.zone !== 'All' ? currentUser?.zone : null;
  const roleLevel = ROLE_HIERARCHY[currentUser?.role] !== undefined ? ROLE_HIERARCHY[currentUser.role] : 6;

  // Permission helpers
  const canView = (featureKey) => {
    if (isAdmin) return true;
    if (!roleFeatures.includes(featureKey)) return false;
    const perm = rolePermissions[featureKey];
    if (!perm) return true; // no explicit permission = full access if feature enabled
    return perm.view !== false;
  };
  const canEdit = (featureKey) => {
    if (isAdmin) return true;
    if (!roleFeatures.includes(featureKey)) return false;
    const perm = rolePermissions[featureKey];
    if (!perm) return true;
    return perm.edit !== false;
  };
  const allowedSubFeatures = (featureKey) => {
    if (isAdmin) return null; // null means all allowed
    const perm = rolePermissions[featureKey];
    if (!perm || !perm.sub_features) return null;
    // sub_features can be array (legacy) or object { key: { view, edit } }
    if (Array.isArray(perm.sub_features)) {
      return perm.sub_features.length === 0 ? null : perm.sub_features;
    }
    // Object format: return keys where view is true
    const keys = Object.keys(perm.sub_features).filter(k => perm.sub_features[k]?.view !== false);
    return keys.length === 0 ? null : keys;
  };
  const canViewSub = (featureKey, subKey) => {
    if (isAdmin) return true;
    const perm = rolePermissions[featureKey];
    if (!perm || !perm.sub_features) return true;
    if (Array.isArray(perm.sub_features)) return perm.sub_features.length === 0 || perm.sub_features.includes(subKey);
    const sub = perm.sub_features[subKey];
    return !sub || sub.view !== false;
  };
  const canEditSub = (featureKey, subKey) => {
    if (isAdmin) return true;
    const perm = rolePermissions[featureKey];
    if (!perm || !perm.sub_features) return true;
    if (Array.isArray(perm.sub_features)) return true; // legacy: no edit info
    const sub = perm.sub_features[subKey];
    return !sub || sub.edit !== false;
  };

  // Sub-user view-only check: returns true when the current user is a sub-user
  // with "view" (read-only) access to the given feature.
  const isViewOnly = (featureKey) => {
    if (!currentUser?.is_sub_user) return false;
    return subUserPermissions[featureKey] === 'view';
  };

  // Memoize the context value so its identity is stable when unrelated state
  // updates. Without this, every setState in AppProvider forces every
  // consumer of useApp() (all ~60 pages/components) to re-render, causing
  // severe UI hangs during tab switches.
  const contextValue = useMemo(() => ({
    currentUser, setCurrentUser, identifyUser, bootstrapFromToken,
    bootstrapped,
    reverifyRequired, setReverifyRequired,
    notifications, fetchNotifications, markNotifRead, markAllRead,
    loading, setLoading, seeded, seedData, api,
    isAdmin, userZone, roleLevel, roleFeatures, rolePermissions,
    departmentFeatures,
    businessLines, selectedBusiness, setSelectedBusiness, allBusinessLines,
    blFeatureMap,
    salesDeptConfig, refreshSalesDeptConfig,
    allPlaces, allPlacesLoading, allPlacesError, allPlacesLoadedAt, refreshBusinessPlaces,
    featureFlags, refreshFeatureFlags,
    canView, canEdit, allowedSubFeatures, canViewSub, canEditSub, isViewOnly,
    subUserPermissions,
    // Browser cache management
    clearAllCache, clearCacheForSection, getCacheStats,
    getCacheTTLs, setUserCacheTTLs, setAdminCacheTTLs, resetUserCacheTTLs,
    DEFAULT_TTLS, SECTION_LABELS,
  }), [
    currentUser, bootstrapped, notifications, loading, seeded, api,
    reverifyRequired,
    isAdmin, userZone, roleLevel, roleFeatures, rolePermissions,
    departmentFeatures,
    businessLines, selectedBusiness, allBusinessLines, blFeatureMap,
    salesDeptConfig, refreshSalesDeptConfig,
    allPlaces, allPlacesLoading, allPlacesError, allPlacesLoadedAt,
    featureFlags, subUserPermissions,
    identifyUser, bootstrapFromToken, fetchNotifications, markNotifRead, markAllRead,
    seedData, refreshBusinessPlaces, refreshFeatureFlags,
  ]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};
